import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Briefcase, Pin, ExternalLink, Link2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** ONE reusable selector. Lists only matters the server already authorised for this user (bootstrap scope). */
export function MatterSelector({ value, onSelect, placeholder = "Search authorised matters…", compact = false }) {
  const { db, scope } = useApp();
  const [q, setQ] = useState("");
  const [recent, setRecent] = useState({ recent: [], pinned: [] });
  useEffect(() => { axios.get(`${API}/context/recent`).then((r) => setRecent(r.data)).catch(() => {}); }, []);
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = db.matters.filter((m) => !s || m.title.toLowerCase().includes(s) || m.caseNumber.toLowerCase().includes(s) || (m.representedEntity || "").toLowerCase().includes(s) || (m.court || "").toLowerCase().includes(s));
    const rank = (m) => (recent.pinned.includes(m.id) ? 0 : recent.recent.includes(m.id) ? 1 : m.leadId === scope.dirId || (m.assignedTo || []).includes(scope.dirId) ? 2 : 3);
    return list.sort((a, b) => rank(a) - rank(b)).slice(0, compact ? 8 : 30);
  }, [db.matters, q, recent, scope.dirId, compact]);
  const togglePin = async (e, id) => { e.stopPropagation(); const { data } = await axios.post(`${API}/context/pin/${id}`); setRecent((r) => ({ ...r, pinned: data.pinned })); };
  return (
    <div data-testid="matter-selector">
      <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} data-testid="matter-selector-search" />
      <div className="nyk-scroll mt-2 max-h-72 divide-y divide-line overflow-y-auto rounded-md border border-line">
        {rows.map((m) => (
          <button key={m.id} onClick={() => onSelect(m)} data-testid={`matter-option-${m.id}`} className={cn("flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface", value === m.id && "bg-copper-light/40")}>
            <Briefcase className="h-4 w-4 shrink-0 text-copper" />
            <div className="min-w-0 flex-1"><div className="truncate font-medium text-ink">{m.title}</div><div className="truncate text-xs text-ink-2">{m.caseNumber} · {m.court}{m.representedEntity ? ` · for ${m.representedEntity}` : ""}</div></div>
            <span className="text-[10px] text-ink-muted">{recent.pinned.includes(m.id) ? "pinned" : recent.recent.includes(m.id) ? "recent" : ""}</span>
            <Pin className={cn("h-3.5 w-3.5", recent.pinned.includes(m.id) ? "text-copper" : "text-ink-muted hover:text-copper")} onClick={(e) => togglePin(e, m.id)} data-testid={`matter-pin-${m.id}`} />
          </button>
        ))}
        {!rows.length && <div className="p-4 text-center text-xs text-ink-muted">No authorised matter matches.</div>}
      </div>
    </div>
  );
}

export function MatterSelectorDialog({ open, onOpenChange, onSelect, title = "Select Matter" }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" aria-describedby="matter-selector-desc"><DialogHeader><DialogTitle className="font-display text-2xl">{title}</DialogTitle><p id="matter-selector-desc" className="text-xs text-ink-2">Only matters you are authorised to see are listed.</p></DialogHeader>
        <MatterSelector onSelect={(m) => { onSelect(m); onOpenChange(false); }} />
      </DialogContent>
    </Dialog>
  );
}

/** Linked-matter chip shown in Drafting / AI Search / Research top bars. */
export function MatterChip({ matterId, onChange, allowChange = true, label = "Matter" }) {
  const { db } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const m = db.matters.find((x) => x.id === matterId);
  useEffect(() => { if (matterId) axios.get(`${API}/context/matter/${matterId}`).catch(() => {}); }, [matterId]);
  return (
    <div className="flex items-center gap-1.5" data-testid="matter-chip">
      {m ? (
        <button onClick={() => navigate(`/matters/${m.id}`)} className="flex max-w-[360px] items-center gap-1.5 rounded-full border border-copper/40 bg-copper-light/40 px-3 py-1 text-xs text-ink hover:border-copper" title="Open Matter" data-testid="matter-chip-open">
          <Briefcase className="h-3.5 w-3.5 text-copper" /><span className="truncate"><b>{label}:</b> {m.title}</span><span className="hidden truncate text-ink-2 sm:inline">· {m.court} · {m.caseNumber}</span><ExternalLink className="h-3 w-3 text-ink-muted" />
        </button>
      ) : <span className="rounded-full border border-dashed border-line px-3 py-1 text-xs text-ink-muted" data-testid="matter-chip-none">No linked matter</span>}
      {allowChange && <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setOpen(true)} data-testid="matter-chip-link"><Link2 className="mr-1 h-3 w-3" />{m ? "Change" : "Link Matter"}</Button>}
      <MatterSelectorDialog open={open} onOpenChange={setOpen} onSelect={(mm) => onChange?.(mm)} title={m ? "Change linked Matter" : "Link Matter"} />
    </div>
  );
}
